import React, { useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { UseAppToast } from "src/common/useAppToast";
import { AdminService } from "src/rest/adminService";
import { StatusCode } from "src/common/constants/constantEnums";

interface AddAdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAdminAdded: () => void;
}

export const AddAdminPanel: React.FC<AddAdminPanelProps> = ({
  isOpen,
  onClose,
  onAdminAdded,
}) => {
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { successToast, errorToast } = UseAppToast();

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Make mobile number required and validate format
    if (!mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobileNo.replace(/\D/g, ""))) {
      newErrors.mobileNo = "Please enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await AdminService.createAdmin(email, mobileNo);
      if (
        response.status === StatusCode.CREATED_STATUS_CODE ||
        response.status === StatusCode.SUCCESS_STATUS_CODE
      ) {
        successToast("Admin created successfully");
        setEmail("");
        setMobileNo("");
        setErrors({});
        onAdminAdded();
        onClose();
      }
    } catch (error: any) {
      // Scan the full response body — backend sometimes returns 401 even for
      // business-logic errors (e.g. "user already exists"), so checking only
      // `data.message` misses cases where the detail lives elsewhere in the body.
      const rawMessage: string =
        error?.response?.data?.message || error?.message || "";
      const fullBody: string = JSON.stringify(error?.response?.data ?? "").toLowerCase();
      const normalized = (rawMessage + " " + fullBody).toLowerCase();

      let friendlyMessage: string;
      const status: number | undefined = error?.response?.status;

      if (
        normalized.includes("already exist") ||
        normalized.includes("duplicate") ||
        normalized.includes("invalid email id")
      ) {
        // Explicit "user exists" keywords in the response body
        friendlyMessage = "An account with this email already exists and cannot be assigned as an admin.";
      } else if (status === 401) {
        // Backend wraps the "user already exists" RuntimeException as 401
        // with a generic "Unauthorized…" message. Since the admin is already
        // authenticated and on the admin panel, 401 on /admin/create means
        // the business logic rejected the email (user already exists).
        friendlyMessage = "An account with this email already exists and cannot be assigned as an admin.";
      } else if (status === 403) {
        friendlyMessage = "You are not authorized to perform this action.";
      } else {
        friendlyMessage = rawMessage || "Failed to create admin. Please try again.";
      }

      errorToast(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setMobileNo("");
    setErrors({});
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight={600} color="#8B0000">
          Add New Admin
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing={6} align="stretch" py={4}>
            {Object.keys(errors).length > 0 && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertDescription fontSize="sm">
                    Please fix the following errors:
                  </AlertDescription>
                  {Object.entries(errors).map(([field, message]) => (
                    <AlertDescription key={field} fontSize="sm" mt={1}>
                      • {message}
                    </AlertDescription>
                  ))}
                </Box>
              </Alert>
            )}

            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontSize="sm" fontWeight={500}>
                Email Address *
              </FormLabel>
              <Input
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    const newErrors = { ...errors };
                    delete newErrors.email;
                    setErrors(newErrors);
                  }
                }}
                borderColor={errors.email ? "#F41E1E" : "#D0CFCF"}
                _focus={{
                  borderColor: errors.email ? "#F41E1E" : "#4F20B8",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                }}
              />
              {errors.email && (
                <Box mt={1} fontSize="sm" color="#F41E1E">
                  {errors.email}
                </Box>
              )}
            </FormControl>

            <FormControl isInvalid={!!errors.mobileNo}>
              <FormLabel fontSize="sm" fontWeight={500}>
                Mobile Number *
              </FormLabel>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={mobileNo}
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  const limited = digitsOnly.slice(0, 10);
                  setMobileNo(limited);
                  if (errors.mobileNo) {
                    const newErrors = { ...errors };
                    delete newErrors.mobileNo;
                    setErrors(newErrors);
                  }
                }}
                borderColor={errors.mobileNo ? "#F41E1E" : "#D0CFCF"}
                _focus={{
                  borderColor: errors.mobileNo ? "#F41E1E" : "#4F20B8",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                }}
              />
              {errors.mobileNo && (
                <Box mt={1} fontSize="sm" color="#F41E1E">
                  {errors.mobileNo}
                </Box>
              )}
            </FormControl>

            <Button
              mt={6}
              bg="#8B0000"
              color="white"
              width="100%"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Creating..."
              _hover={{ bg: "#6B0000" }}
              _active={{ bg: "#5B0000" }}
              fontSize="sm"
              fontWeight={600}
            >
              Submit
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default AddAdminPanel;
